import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { recoveryCodeSchema, twoFactorCodeSchema } from '@blimo/shared';
import { Form, FormRootError, InputField, applyProblemToForm } from '../components/form.jsx';
import { Button } from '../components/button.jsx';

/**
 * @param {{ onSubmit: (payload: { code?: string, recovery_code?: string }) => Promise<unknown> }} props
 */
export function TwoFactorChallengeForm({ onSubmit }) {
  const [mode, setMode] = useState(/** @type {'code'|'recovery'} */ ('code'));
  return mode === 'code' ? (
    <CodeForm key="code" onSubmit={onSubmit} onSwitch={() => setMode('recovery')} />
  ) : (
    <RecoveryForm key="recovery" onSubmit={onSubmit} onSwitch={() => setMode('code')} />
  );
}

function CodeForm({ onSubmit, onSwitch }) {
  const form = useForm({ resolver: zodResolver(twoFactorCodeSchema), defaultValues: { code: '' } });
  async function submit(values) {
    try {
      await onSubmit({ code: values.code });
    } catch (error) {
      applyProblemToForm(error, form.setError, { fields: ['code'] });
      form.resetField('code');
    }
  }
  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(submit)} className="flex flex-col gap-4" noValidate>
        <FormRootError />
        <InputField
          name="code"
          label="Bestätigungscode"
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={6}
          autoFocus
          description="6-stelliger Code aus Ihrer Authenticator-App"
        />
        <Button type="submit" loading={form.formState.isSubmitting} className="w-full">
          Bestätigen
        </Button>
        <Button variant="link" onClick={onSwitch} className="self-center">
          Wiederherstellungscode verwenden
        </Button>
      </form>
    </Form>
  );
}

function RecoveryForm({ onSubmit, onSwitch }) {
  const form = useForm({ resolver: zodResolver(recoveryCodeSchema), defaultValues: { recovery_code: '' } });
  async function submit(values) {
    try {
      await onSubmit({ recovery_code: values.recovery_code });
    } catch (error) {
      applyProblemToForm(error, form.setError, { fields: ['recovery_code'] });
    }
  }
  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(submit)} className="flex flex-col gap-4" noValidate>
        <FormRootError />
        <InputField name="recovery_code" label="Wiederherstellungscode" autoComplete="off" autoFocus />
        <Button type="submit" loading={form.formState.isSubmitting} className="w-full">
          Bestätigen
        </Button>
        <Button variant="link" onClick={onSwitch} className="self-center">
          Code aus der Authenticator-App verwenden
        </Button>
      </form>
    </Form>
  );
}
